import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, X, Search, Send, LifeBuoy, AlertTriangle, ArrowLeft, KanbanSquare, Lock,
  Play, UserCheck, CheckCheck, Check, Archive, ExternalLink, Paperclip, History,
  MessageSquare, Download, Upload, RotateCcw, Clock, Building2, Package as PackageIcon,
  FileText, Users, Inbox, Phone, Mail, Globe, Loader2, Eye, ChevronRight, Calendar, Sparkles, UserRound,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/common";
import PaketPruefung from "@/components/PaketPruefung";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import {
  StatusPill, PrioPill, KundeAmZug, SlaPill, PRIORITIES,
  fmtDateTime, fmtDate, slaCountdown, fmtSize, inputCls,
} from "./supportUtils";
import { useI18n } from "@/context/I18nContext";

const KPIS = [
  ["gesamt", "Supportfälle", "alle"],
  ["neu", "Neu", "neu"],
  ["in_bearbeitung", "In Bearbeitung", "in_bearbeitung"],
  ["warten_auf_kunde", "Warten auf Kunde", "warten_auf_kunde"],
  ["warten_auf_kewag", "Warten auf KEWAG", "warten_auf_kewag"],
  ["hoch", "Hohe Priorität", "hoch"],
  ["erledigt", "Erledigt", "erledigt"],
  ["sla_gefaehrdet", "SLA gefährdet", "sla"],
];

const TEMPLATES = [
  ["", "— Vorlage wählen —"],
  ["Vielen Dank für Ihre Nachricht. Wir kümmern uns umgehend darum und melden uns in Kürze bei Ihnen.", "Eingang bestätigen"],
  ["Wir haben Ihr Anliegen erledigt. Bitte prüfen Sie das Ergebnis und geben Sie uns kurz Bescheid, falls noch etwas offen ist.", "Erledigt melden"],
  ["Damit wir Ihnen rasch weiterhelfen können, haben wir eine kurze Rückfrage: ", "Rückfrage stellen"],
  ["Ihre Anfrage ist bei uns eingegangen und wird bearbeitet. Wir halten Sie auf dem Laufenden.", "In Bearbeitung"],
];

const EMPTY_NEW = {
  client_id: "", subject: "", message: "", category: "allgemein",
  priority: "mittel", status: "neu", assignee: "nicht zugewiesen", due_date: "", internal_note: "",
};

export default function Support() {
  const { t: tr } = useI18n();
  const nav = useNavigate();
  const newTicketBtnRef = useRef(null);
  const [tickets, setTickets] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [clients, setClients] = useState([]);
  const [detail, setDetail] = useState(null);
  const [selId, setSelId] = useState(null);
  const [params, setParams] = useSearchParams();
  const [showNew, setShowNew] = useState(false);
  const [showTask, setShowTask] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("alle");
  const [fPrio, setFPrio] = useState("alle");
  const [fAssignee, setFAssignee] = useState("alle");
  const [fClient, setFClient] = useState("alle");
  const [fPackage, setFPackage] = useState("alle");
  const [fSla, setFSla] = useState("alle");
  const [fDate, setFDate] = useState("alle");
  const [sort, setSort] = useState("neueste");

  const load = () => api.get("/support").then((r) => setTickets(r.data));
  const loadDetail = (id) => api.get(`/support/${id}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));

  useEffect(() => {
    load();
    api.get("/support/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (params.get("new") === "1") { setShowNew(true); params.delete("new"); setParams(params, { replace: true }); }
  }, [params, setParams]);
  useEffect(() => { if (selId) loadDetail(selId); else setDetail(null); }, [selId]);

  const canWrite = !!cfg?.can_write;

  const summary = useMemo(() => {
    const s = { gesamt: 0, neu: 0, in_bearbeitung: 0, warten_auf_kunde: 0, warten_auf_kewag: 0, loesung_vorgeschlagen: 0, hoch: 0, erledigt: 0, archiviert: 0, sla_gefaehrdet: 0 };
    (tickets || []).forEach((t) => {
      s.gesamt++;
      if (s[t.status] !== undefined) s[t.status]++;
      if (["hoch", "kritisch"].includes(t.priority) && t.open) s.hoch++;
      if (t.open && t.sla_at_risk) s.sla_gefaehrdet++;
    });
    return s;
  }, [tickets]);

  const packages = useMemo(() => Array.from(new Set((tickets || []).map((t) => t.package_name).filter(Boolean))).sort(), [tickets]);

  const shown = useMemo(() => {
    if (!tickets) return [];
    const ql = q.trim().toLowerCase();
    let arr = tickets.filter((t) => {
      if (fStatus === "hoch") { if (!(["hoch", "kritisch"].includes(t.priority) && t.open)) return false; }
      else if (fStatus === "offen") { if (!t.open) return false; }
      else if (fStatus !== "alle" && t.status !== fStatus) return false;
      if (fPrio !== "alle" && t.priority !== fPrio) return false;
      if (fAssignee !== "alle" && t.assignee !== fAssignee) return false;
      if (fClient !== "alle" && t.client_id !== fClient) return false;
      if (fPackage !== "alle" && t.package_name !== fPackage) return false;
      if (fSla === "at_risk" && !(t.open && t.sla_at_risk)) return false;
      else if (["ueberfaellig", "bald_faellig", "im_zeitplan"].includes(fSla) && t.sla_state !== fSla) return false;
      if (fDate !== "alle") {
        const days = fDate === "heute" ? 1 : fDate === "7" ? 7 : 30;
        const ref = new Date(t.last_at || t.created_at);
        if (Date.now() - ref > days * 86400000) return false;
      }
      if (ql) {
        const hay = `${t.subject} ${t.company_name} ${t.ticket_no} ${t.last_message} ${t.assignee}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
    const prioRank = { kritisch: 0, hoch: 1, mittel: 2, niedrig: 3 };
    const slaRank = { ueberfaellig: 0, bald_faellig: 1, im_zeitplan: 2, wartet_kunde: 3, kein: 4 };
    return arr.slice().sort((a, b) => {
      if (sort === "prioritaet") return (prioRank[a.priority] ?? 9) - (prioRank[b.priority] ?? 9) || (b.last_at || "").localeCompare(a.last_at || "");
      if (sort === "sla") return (slaRank[a.sla_state] ?? 9) - (slaRank[b.sla_state] ?? 9) || (b.last_at || "").localeCompare(a.last_at || "");
      if (sort === "kunde") return (a.company_name || "").localeCompare(b.company_name || "");
      return (b.last_at || "").localeCompare(a.last_at || "");
    });
  }, [tickets, q, fStatus, fPrio, fAssignee, fClient, fPackage, fSla, fDate, sort]);

  useEffect(() => {
    if (!tickets) return;
    if (selId && shown.some((t) => t.id === selId)) return;
    setSelId(shown.length > 0 ? shown[0].id : null);
  }, [shown, tickets]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtersActive = q || [fStatus, fPrio, fAssignee, fClient, fPackage, fSla, fDate].some((v) => v !== "alle");
  const resetFilters = () => { setQ(""); setFStatus("alle"); setFPrio("alle"); setFAssignee("alle"); setFClient("alle"); setFPackage("alle"); setFSla("alle"); setFDate("alle"); setSort("neueste"); };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/support/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = `support-export-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
      URL.revokeObjectURL(url);
      toast.success(tr("Export erstellt"));
    } catch { toast.error(tr("Export fehlgeschlagen.")); }
    finally { setExporting(false); }
  };

  const afterMutation = () => { load(); if (selId) loadDetail(selId); };

  if (!tickets || !cfg) return <Spinner />;

  const selSlim = tickets.find((t) => t.id === selId);

  return (
    <div className="fade-in">
      <PageHeader title={tr("Support")} subtitle={tr("Ein echtes Ticket-System — keine Anfrage geht verloren.")}>
        <div className="flex items-center gap-2.5">
          <button onClick={doExport} disabled={exporting} data-testid="support-export-btn"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {tr("Exportieren")}
          </button>
          {canWrite && (
            <button ref={newTicketBtnRef} onClick={() => setShowNew(true)} data-testid="new-ticket-btn"
              className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg transition-colors inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> {tr("Neuer Supportfall")}
            </button>
          )}
        </div>
      </PageHeader>

      {!canWrite && (
        <div className="mb-5 flex items-center gap-2 text-sm text-amber-300 bg-amber-400/10 border border-amber-400/20 rounded-lg px-4 py-2.5" data-testid="support-readonly-banner">
          <Eye className="w-4 h-4 shrink-0" /> {tr("Nur-Lese-Zugriff — Ihre Rolle darf Supportfälle ansehen, aber nicht bearbeiten.")}
        </div>
      )}

      {/* ===== KPI-Leiste ===== */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2.5 mb-5" data-testid="support-summary">
        {KPIS.map(([k, lbl, fval]) => {
          const active = fStatus === fval;
          const danger = k === "sla_gefaehrdet";
          return (
            <button key={k} onClick={() => setFStatus(fval)} data-testid={`support-stat-${k}`}
              className={`text-left p-3.5 rounded-xl border transition-all ${active ? "border-[#A3E635]/60 bg-[#A3E635]/[0.08]" : "border-white/8 bg-[#121217] hover:border-white/20"}`}>
              <div className={`text-[26px] leading-7 font-black tracking-tight ${danger && summary[k] > 0 ? "text-red-300" : active ? "text-[#A3E635]" : "text-white"}`}>{summary[k]}</div>
              <div className="text-[11px] text-gray-400 mt-1 leading-tight">{tr(lbl)}</div>
            </button>
          );
        })}
      </div>

      {/* ===== Filter & Suche ===== */}
      <Card className="p-3 mb-5" data-testid="support-filters">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="support-search"
              placeholder={tr("Betreff, Kunde, Ticket-Nr., E-Mail…")} className={`${inputCls} pl-9`} />
          </div>
          <FSelect testid="filter-status" value={fStatus} onChange={setFStatus} options={[["alle", tr("Status: Alle")], ["offen", tr("Nur offene")], ...cfg.status_order.map((s) => [s, tr(cfg.status_labels[s])])]} />
          <FSelect testid="filter-priority" value={fPrio} onChange={setFPrio} options={[["alle", tr("Priorität: Alle")], ...PRIORITIES.map(([k, v]) => [k, tr(v)])]} />
          <FSelect testid="filter-assignee" value={fAssignee} onChange={setFAssignee} options={[["alle", tr("Zuständig: Alle")], ...cfg.assignees.map((a) => [a, tr(a)])]} />
          <FSelect testid="filter-client" value={fClient} onChange={setFClient} options={[["alle", tr("Kunde: Alle")], ...clients.map((c) => [c.id, c.company_name])]} />
          <FSelect testid="filter-package" value={fPackage} onChange={setFPackage} options={[["alle", tr("Paket: Alle")], ...packages.map((p) => [p, p])]} />
          <FSelect testid="filter-sla" value={fSla} onChange={setFSla} options={[["alle", tr("SLA: Alle")], ["at_risk", tr("Gefährdet")], ["ueberfaellig", tr("Überfällig")], ["bald_faellig", tr("Bald fällig")], ["im_zeitplan", tr("Im Zeitplan")]]} />
          <FSelect testid="filter-date" value={fDate} onChange={setFDate} options={[["alle", tr("Zeitraum: Alle")], ["heute", tr("Heute")], ["7", tr("7 Tage")], ["30", tr("30 Tage")]]} />
          <FSelect testid="filter-sort" value={sort} onChange={setSort} options={[["neueste", tr("Neueste zuerst")], ["prioritaet", tr("Nach Priorität")], ["sla", tr("Nach SLA")], ["kunde", tr("Nach Kunde")]]} />
          {filtersActive && (
            <button onClick={resetFilters} data-testid="filter-reset" className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5">
              <RotateCcw className="w-3.5 h-3.5" /> {tr("Zurücksetzen")}
            </button>
          )}
        </div>
      </Card>

      {/* ===== 3-Spalten-Arbeitsbereich ===== */}
      <div className="grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)_312px] gap-4 xl:h-[calc(100vh-330px)] xl:min-h-[600px]">
        {/* Queue */}
        <div className={`min-h-0 ${selId ? "hidden xl:flex" : "flex"} flex-col`} data-testid="support-queue">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 px-1 mb-2 flex items-center justify-between">
            <span>{tr("Ticket-Queue")}</span><span>{shown.length}</span>
          </div>
          <div className="min-h-0 flex-1 xl:overflow-y-auto space-y-2 pr-0.5">
            {shown.length === 0 ? <Empty text={tr("Keine Supportfälle in dieser Ansicht.")} /> : shown.map((t) => (
              <QueueCard key={t.id} t={t} active={selId === t.id} onClick={() => setSelId(t.id)} tr={tr} />
            ))}
          </div>
        </div>

        {/* Detail / Arbeitsbereich */}
        <div className={`min-h-0 ${selId ? "flex" : "hidden xl:flex"} flex-col`}>
          {!detail || detail.id !== selId ? (
            <Card className="flex-1 flex items-center justify-center text-gray-600 text-sm min-h-[40vh]">
              {selId ? <Loader2 className="w-6 h-6 animate-spin" /> : <div className="text-center"><LifeBuoy className="w-8 h-8 mx-auto mb-3 text-gray-700" />{tr("Wählen Sie links einen Supportfall aus.")}</div>}
            </Card>
          ) : (
            <TicketWorkspace key={detail.id} t={detail} cfg={cfg} canWrite={canWrite} nav={nav}
              onBack={() => setSelId(null)} onMutated={afterMutation} onTask={() => setShowTask(true)} />
          )}
        </div>

        {/* Kontextpanel */}
        <div className={`min-h-0 ${selId && detail ? "block" : "hidden xl:block"} xl:overflow-y-auto pr-0.5`}>
          {detail && detail.id === selId
            ? <ContextPanel t={detail} nav={nav} canWrite={canWrite} onMutated={afterMutation} tr={tr} />
            : <Card className="p-6 text-center text-gray-600 text-sm">{tr("Kontext erscheint hier.")}</Card>}
        </div>
      </div>

      {showNew && (
        <NewTicketModal cfg={cfg} clients={clients}
          onClose={() => setShowNew(false)}
          returnFocusRef={newTicketBtnRef}
          onCreated={(tk) => { setShowNew(false); load(); setSelId(tk.id); }} />
      )}
      {showTask && detail && (
        <TaskModal t={detail} onClose={() => setShowTask(false)}
          onDone={() => { setShowTask(false); afterMutation(); }} />
      )}
    </div>
  );
}

const FSelect = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="bg-black border border-white/10 rounded-lg px-2.5 py-2 text-xs text-gray-200 focus:outline-none focus:border-[#A3E635] transition-colors max-w-[180px]">
    {options.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
  </select>
);

// ---------------- Queue-Karte ----------------
function QueueCard({ t, active, onClick, tr }) {
  return (
    <button onClick={onClick} data-testid={`ticket-row-${t.id}`}
      className={`w-full text-left p-3.5 rounded-xl border transition-all ${active ? "border-[#A3E635]/60 bg-[#A3E635]/[0.07] ring-1 ring-[#A3E635]/30" : "border-white/8 bg-[#121217] hover:border-white/20"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`font-semibold text-sm truncate ${active ? "text-white" : "text-gray-100"}`}>{t.subject}</div>
          <div className="text-[11px] text-gray-500 mt-0.5 truncate">{t.company_name} · <span className="font-mono">{t.ticket_no}</span></div>
        </div>
        {t.unread > 0 && <span className="bg-[#A3E635] text-black text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center shrink-0">{t.unread}</span>}
      </div>
      <div className="text-xs text-gray-400 mt-1.5 line-clamp-2">{t.last_message}</div>
      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
        <StatusPill status={t.status} label={tr(t.status_label || t.status)} />
        <PrioPill p={t.priority} label={tr(t.priority_label || t.priority)} />
        {t.open && <SlaPill state={t.sla_state} label={tr(t.sla_label || "")} />}
        {t.kunde_am_zug && <KundeAmZug />}
        {t.billing_status === "offen_pruefen" && (
          <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 font-semibold">{tr("Paket prüfen")}</span>
        )}
      </div>
      <div className="text-[10px] text-gray-600 mt-2 flex items-center gap-1.5">
        <Clock className="w-3 h-3" /> {fmtDateTime(t.last_at)} · {tr(t.assignee)}
      </div>
    </button>
  );
}

// ---------------- Ticket-Arbeitsbereich (Mitte) ----------------
const TABS = [
  ["konversation", "Konversation", MessageSquare],
  ["notizen", "Interne Notizen", Lock],
  ["aufgaben", "Aufgaben", KanbanSquare],
  ["dateien", "Dateien", Paperclip],
  ["aktivitaet", "Aktivität", History],
];

function TicketWorkspace({ t, cfg, canWrite, nav, onBack, onMutated, onTask }) {
  const { t: tr } = useI18n();
  const [tab, setTab] = useState("konversation");
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [pendingAtt, setPendingAtt] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const replyRef = useRef(null);
  const fileRef = useRef(null);
  const dateiRef = useRef(null);
  const [aiConv, setAiConv] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const loadAi = async () => {
    if (aiConv) { setAiOpen((o) => !o); return; }
    try { const { data } = await api.get(`/support/${t.id}/ai-conversation`); setAiConv(data); setAiOpen(true); }
    catch { toast.error(tr("KI-Dialog konnte nicht geladen werden.")); }
  };

  const put = async (fields, msg) => {
    setBusy(true);
    try { await api.put(`/support/${t.id}`, fields); toast.success(msg || tr("Gespeichert")); onMutated(); }
    catch (e) { toast.error(e.response?.data?.detail || tr("Konnte nicht speichern.")); }
    finally { setBusy(false); }
  };
  const quick = (status, msg) => put({ status }, msg || tr("Status aktualisiert"));

  const uploadFile = async (file, asPending) => {
    if (!file) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/support/${t.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (asPending) setPendingAtt(data); else { toast.success(tr("Datei hochgeladen")); onMutated(); }
      return data;
    } catch (e) { toast.error(e.response?.data?.detail || tr("Upload fehlgeschlagen.")); return null; }
    finally { setUploading(false); }
  };

  const downloadAtt = async (aid, name) => {
    try {
      const res = await api.get(`/support/${t.id}/attachment/${aid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = name || "datei"; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(tr("Download fehlgeschlagen.")); }
  };

  const sendReply = async () => {
    if (!reply.trim()) { toast.error(tr("Antwort darf nicht leer sein.")); return; }
    setBusy(true);
    try {
      await api.post(`/support/${t.id}/reply`, {
        text: reply, is_internal: internal,
        attachment_id: pendingAtt?.id || null, attachment_label: pendingAtt?.file_name || "",
      });
      setReply(""); setPendingAtt(null);
      toast.success(internal ? tr("Interne Notiz gespeichert") : tr("Antwort gesendet"));
      onMutated();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Konnte nicht senden.")); }
    finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.post(`/support/${t.id}/note`, { text: note }); setNote(""); toast.success(tr("Interne Notiz gespeichert")); onMutated(); }
    catch { toast.error(tr("Konnte Notiz nicht speichern.")); }
    finally { setBusy(false); }
  };

  const focusReply = () => { setTab("konversation"); setInternal(false); setTimeout(() => replyRef.current?.focus(), 50); };

  return (
    <Card className="p-0 min-h-0 flex flex-col overflow-hidden" data-testid="ticket-detail">
      {/* Kopf + Toolbar */}
      <div className="p-4 border-b border-white/8 shrink-0">
        <button onClick={onBack} className="xl:hidden inline-flex items-center gap-2 text-xs text-gray-400 hover:text-white mb-2" data-testid="ticket-back">
          <ArrowLeft className="w-4 h-4" /> {tr("Zurück zur Queue")}
        </button>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-black tracking-tight truncate">{t.subject}</h2>
              <StatusPill status={t.status} label={tr(t.status_label || t.status)} testid="ticket-status-badge" />
              <PrioPill p={t.priority} label={tr(t.priority_label || t.priority)} />
              {t.open && <SlaPill state={t.sla_state} label={tr(t.sla_label || "")} />}
              {t.human_requested && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border border-[#A3E635]/50 bg-[#A3E635]/15 text-[#A3E635]" data-testid="ticket-human-badge">
                  <UserRound className="w-3 h-3" /> {tr("Mensch gewünscht")}
                </span>
              )}
              {t.source === "customer_cockpit_ai" && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border border-violet-400/40 bg-violet-400/10 text-violet-300" data-testid="ticket-ai-badge">
                  <Sparkles className="w-3 h-3" /> {tr("KI-Assistent")}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              <span className="font-mono">{t.ticket_no}</span> · {t.company_name} · {tr(t.category_label || t.category)} · {tr("Zuständig:")} {tr(t.assignee)}
            </div>
          </div>
        </div>

        {/* Steuern + Aktionen */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <select value={t.status} disabled={!canWrite || busy} onChange={(e) => quick(e.target.value)} data-testid="td-status"
            className="bg-black border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#A3E635] disabled:opacity-50">
            {cfg.status_order.map((s) => <option key={s} value={s}>{tr(cfg.status_labels[s])}</option>)}
          </select>
          <select value={t.priority} disabled={!canWrite || busy} onChange={(e) => put({ priority: e.target.value }, tr("Priorität aktualisiert"))} data-testid="td-priority"
            className="bg-black border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#A3E635] disabled:opacity-50">
            {PRIORITIES.map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}
          </select>
          <select value={t.assignee} disabled={!canWrite || busy} onChange={(e) => put({ assignee: e.target.value }, tr("Zugewiesen"))} data-testid="td-assignee"
            className="bg-black border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#A3E635] disabled:opacity-50">
            {cfg.assignees.map((a) => <option key={a} value={a}>{tr(a)}</option>)}
          </select>
          {canWrite && <>
            <ToolBtn icon={Send} testid="td-reply-btn" onClick={focusReply}>{tr("Antworten")}</ToolBtn>
            <ToolBtn icon={KanbanSquare} testid="td-act-task" onClick={onTask}>{tr("Aufgabe")}</ToolBtn>
            <ToolBtn icon={Check} testid="td-act-done" accent onClick={() => quick("erledigt", tr("Als erledigt markiert"))}>{tr("Schliessen")}</ToolBtn>
            <ToolBtn icon={Archive} testid="td-act-archive" onClick={() => quick("archiviert", tr("Archiviert"))}>{tr("Archivieren")}</ToolBtn>
          </>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-3 pt-2 border-b border-white/8 shrink-0 overflow-x-auto">
        {TABS.map(([k, lbl, Icon]) => {
          const count = k === "notizen" ? (t.internal_notes || []).length : k === "aufgaben" ? (t.tasks || []).length : k === "dateien" ? (t.attachments || []).length : 0;
          return (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-[#A3E635] text-[#A3E635]" : "border-transparent text-gray-500 hover:text-gray-300"}`}>
              <Icon className="w-3.5 h-3.5" /> {tr(lbl)}{count > 0 && <span className="text-[10px] text-gray-500">({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Tab-Inhalt */}
      <div className="flex-1 min-h-0 xl:overflow-y-auto p-4" data-testid={`tabpanel-${tab}`}>
        {tab === "konversation" && (
          <div className="space-y-3" data-testid="ticket-thread">
            {t.source === "customer_cockpit_ai" && (t.ai_summary || t.ai_conversation_id) && (
              <div className="rounded-xl border border-violet-400/30 bg-violet-400/[0.06] p-3.5 mb-1" data-testid="ticket-ai-summary">
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkles className="w-4 h-4 text-violet-300" />
                  <span className="text-xs font-bold uppercase tracking-wider text-violet-300">{tr("KI-Zusammenfassung")}</span>
                  {t.language && <span className="text-[10px] text-gray-500 uppercase">· {t.language}</span>}
                </div>
                {t.ai_summary && <p className="text-sm text-gray-200 whitespace-pre-wrap">{t.ai_summary}</p>}
                {t.ai_next_step && <p className="text-xs text-gray-400 mt-1.5"><span className="font-semibold">{tr("Nächster Schritt")}:</span> {t.ai_next_step}</p>}
                {t.ai_conversation_id && (
                  <button onClick={loadAi} data-testid="ticket-ai-transcript-btn" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-violet-300 hover:text-violet-200">
                    <MessageSquare className="w-3.5 h-3.5" /> {aiOpen ? tr("KI-Dialog ausblenden") : tr("KI-Dialog ansehen")}
                  </button>
                )}
                {aiOpen && aiConv && (
                  <div className="mt-3 space-y-2 border-t border-white/10 pt-3" data-testid="ticket-ai-transcript">
                    {(aiConv.messages || []).map((m, i) => (
                      <div key={i} className="text-xs">
                        <span className={`font-bold ${m.sender_type === "customer" ? "text-gray-300" : "text-violet-300"}`}>{m.sender_type === "customer" ? tr("Kunde") : tr("KI-Assistent")}:</span>{" "}
                        <span className="text-gray-400 whitespace-pre-wrap">{m.message}</span>
                      </div>
                    ))}
                    {(aiConv.messages || []).length === 0 && <p className="text-xs text-gray-600">{tr("Kein Dialogverlauf vorhanden.")}</p>}
                  </div>
                )}
              </div>
            )}
            {(t.messages || []).map((m, i) => {
              const mine = m.sender_role !== "client" && m.sender_role !== "client_staff";
              return (
                <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-200"}`}>
                    <div className="text-[10px] opacity-70 mb-0.5 font-semibold">{m.sender_name} · {fmtDateTime(m.created_at)} · {mine ? tr("KEWAG") : tr("Kunde")}</div>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    {m.attachment_id ? (
                      <button onClick={() => downloadAtt(m.attachment_id, m.attachment_label)}
                        className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold underline ${mine ? "text-black/80" : "text-[#A3E635]"}`}>
                        <Paperclip className="w-3 h-3" /> {m.attachment_label || tr("Anhang")}
                      </button>
                    ) : m.attachment_url ? (
                      <a href={m.attachment_url} target="_blank" rel="noreferrer" className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold underline ${mine ? "text-black/80" : "text-[#A3E635]"}`}>
                        <Paperclip className="w-3 h-3" /> {m.attachment_label || tr("Anhang öffnen")}
                      </a>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {(t.messages || []).length === 0 && <p className="text-sm text-gray-600 text-center py-6">{tr("Noch keine Nachrichten.")}</p>}
          </div>
        )}

        {tab === "notizen" && (
          <div data-testid="ticket-notes">
            <p className="text-[11px] text-amber-300/80 mb-3 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {tr("Nur für KEWAG sichtbar — der Kunde sieht diese Notizen niemals.")}</p>
            <div className="space-y-2">
              {(t.internal_notes || []).length === 0 ? <p className="text-xs text-gray-600">{tr("Noch keine internen Notizen.")}</p> : (t.internal_notes || []).map((n) => (
                <div key={n.id} className="text-sm bg-amber-400/[0.06] border border-amber-400/20 rounded-lg px-3 py-2">
                  <div className="text-gray-200 whitespace-pre-wrap">{n.text}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{n.author} · {fmtDateTime(n.created_at)}</div>
                </div>
              ))}
            </div>
            {canWrite && (
              <div className="flex gap-2 mt-3">
                <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="note-input"
                  placeholder={tr("Interne Notiz hinzufügen…")} className={inputCls} />
                <button onClick={addNote} disabled={busy || !note.trim()} data-testid="note-add"
                  className="shrink-0 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold px-4 rounded-lg disabled:opacity-40">{tr("Notiz")}</button>
              </div>
            )}
          </div>
        )}

        {tab === "aufgaben" && (
          <div data-testid="ticket-tasks">
            {(t.tasks || []).length === 0 ? <p className="text-sm text-gray-600">{tr("Noch keine verknüpften Aufgaben.")}</p> : (
              <div className="space-y-2">
                {(t.tasks || []).map((tk) => (
                  <button key={tk.id} onClick={() => nav(`/admin/tasks?focus=${tk.id}`)} className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold truncate">{tk.title}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 bg-black text-gray-300 shrink-0">{tr(tk.status)}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{tr("Fällig")}: {fmtDate(tk.due_date)} · {tr(tk.assignee)}</div>
                  </button>
                ))}
              </div>
            )}
            {canWrite && (
              <button onClick={onTask} data-testid="tasks-create-btn" className="mt-3 inline-flex items-center gap-2 bg-[#A3E635]/10 hover:bg-[#A3E635]/20 text-[#A3E635] font-bold text-sm py-2 px-4 rounded-lg">
                <Plus className="w-4 h-4" /> {tr("Aufgabe erstellen")}
              </button>
            )}
          </div>
        )}

        {tab === "dateien" && (
          <div data-testid="ticket-files">
            {(t.attachments || []).length === 0 ? <p className="text-sm text-gray-600">{tr("Noch keine Dateien angehängt.")}</p> : (
              <div className="space-y-2">
                {(t.attachments || []).map((a) => (
                  <div key={a.id} data-testid={`file-row-${a.id}`} className="flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <FileText className="w-4 h-4 text-[#A3E635] shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{a.file_name}</div>
                        <div className="text-[10px] text-gray-500">{fmtSize(a.file_size)} · {a.uploaded_by_name} · {fmtDateTime(a.created_at)}</div>
                      </div>
                    </div>
                    <button onClick={() => downloadAtt(a.id, a.file_name)} data-testid={`file-download-${a.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg">
                      <Download className="w-3.5 h-3.5" /> {tr("Download")}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {canWrite && (
              <div className="mt-3">
                <input ref={dateiRef} type="file" className="hidden" data-testid="file-upload-input"
                  onChange={(e) => { uploadFile(e.target.files?.[0], false); e.target.value = ""; }} />
                <button onClick={() => dateiRef.current?.click()} disabled={uploading} data-testid="file-upload-btn"
                  className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 font-bold text-sm py-2 px-4 rounded-lg disabled:opacity-50">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {tr("Datei hochladen")}
                </button>
                <p className="text-[10px] text-gray-600 mt-1.5">{tr("Erlaubt: PDF, Bilder, Office, ZIP, TXT — max. 15 MB.")}</p>
              </div>
            )}
          </div>
        )}

        {tab === "aktivitaet" && (
          <div data-testid="ticket-activity">
            {(t.activity || []).length === 0 ? <p className="text-sm text-gray-500">{tr("Noch keine Einträge.")}</p> : (
              <div className="space-y-2.5">
                {t.activity.map((a) => (
                  <div key={a.id} className="flex gap-3 text-sm border-b border-white/5 pb-2.5 last:border-0">
                    <div className="w-2 h-2 rounded-full bg-[#A3E635] mt-1.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium">{a.action}</div>
                      {a.changes && <div className="text-xs text-gray-500 mt-0.5">{renderChanges(a.changes)}</div>}
                      <div className="text-[11px] text-gray-600 mt-0.5">{a.actor_name} · {fmtDateTime(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Antwort-Editor (nur Konversation) */}
      {tab === "konversation" && canWrite && (
        <div className="border-t border-white/8 p-3 shrink-0 bg-[#0e0e12]" data-testid="reply-editor">
          <div className="flex items-center gap-2 mb-2">
            <div className="inline-flex rounded-lg bg-black border border-white/10 p-0.5">
              <button onClick={() => setInternal(false)} data-testid="reply-mode-public" className={`px-3 py-1 rounded-md text-xs font-bold ${!internal ? "bg-[#A3E635] text-black" : "text-gray-400"}`}>{tr("Öffentliche Antwort")}</button>
              <button onClick={() => setInternal(true)} data-testid="reply-mode-internal" className={`px-3 py-1 rounded-md text-xs font-bold ${internal ? "bg-amber-400 text-black" : "text-gray-400"}`}>{tr("Interne Notiz")}</button>
            </div>
            {!internal && (
              <select onChange={(e) => { if (e.target.value) setReply((r) => (r ? r + "\n" : "") + tr(e.target.value)); e.target.value = ""; }} data-testid="reply-template"
                className="bg-black border border-white/10 rounded-lg px-2 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-[#A3E635]">
                {TEMPLATES.map(([v, lbl], i) => <option key={i} value={v}>{tr(lbl)}</option>)}
              </select>
            )}
          </div>
          <textarea ref={replyRef} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="reply-input" rows={3}
            placeholder={internal ? tr("Interne Notiz schreiben…") : tr("Antwort an den Kunden schreiben…")}
            className={`${inputCls} ${internal ? "border-amber-400/30" : ""}`} />
          {pendingAtt && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5" data-testid="reply-pending-att">
              <Paperclip className="w-3.5 h-3.5 text-[#A3E635]" /> {pendingAtt.file_name}
              <button onClick={() => setPendingAtt(null)} className="text-gray-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            <div>
              <input ref={fileRef} type="file" className="hidden" data-testid="reply-attach-input"
                onChange={(e) => { uploadFile(e.target.files?.[0], true); e.target.value = ""; }} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading || internal} data-testid="reply-attach-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg disabled:opacity-40">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} {tr("Datei anhängen")}
              </button>
            </div>
            <button onClick={sendReply} disabled={busy || !reply.trim()} data-testid="reply-send"
              className={`font-bold py-2 px-5 rounded-lg inline-flex items-center gap-2 disabled:opacity-40 ${internal ? "bg-amber-400 hover:bg-amber-300 text-black" : "bg-[#A3E635] hover:bg-[#BEF264] text-black"}`}>
              <Send className="w-4 h-4" /> {internal ? tr("Notiz speichern") : tr("Antwort senden")}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

const ToolBtn = ({ icon: Icon, children, onClick, testid, accent }) => (
  <button onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${accent ? "bg-[#A3E635]/10 hover:bg-[#A3E635]/20 text-[#A3E635]" : "bg-white/5 hover:bg-white/10 text-gray-200"}`}>
    <Icon className="w-3.5 h-3.5" /> {children}
  </button>
);

// ---------------- Kontextpanel (rechts) ----------------
function ContextPanel({ t, nav, canWrite, onMutated, tr }) {
  const c = t.customer || {};
  const p = c.package || {};
  return (
    <div className="space-y-3" data-testid="context-panel">
      {/* Ticket-Infos */}
      <Card className="p-4" data-testid="ctx-ticket">
        <CtxHead icon={LifeBuoy}>{tr("Ticket-Informationen")}</CtxHead>
        <Row k={tr("Erstellt am")} v={fmtDateTime(t.created_at)} />
        <Row k={tr("Letzte Aktivität")} v={fmtDateTime(t.last_at)} />
        <Row k={tr("Erstellt von")} v={t.created_by_name || "—"} />
        <div className="flex items-center justify-between py-1.5">
          <span className="text-[12px] text-gray-500">{tr("SLA-Status")}</span>
          {t.open && t.sla_state && t.sla_state !== "kein"
            ? <SlaPill state={t.sla_state} label={tr(t.sla_label || "")} testid="ctx-sla" />
            : <span className="text-[12px] text-gray-500">—</span>}
        </div>
        {t.open && t.sla_due_at && <Row k={tr("Antwortfrist")} v={`${fmtDateTime(t.sla_due_at)} · ${slaCountdown(t.sla_due_at)}`} />}
      </Card>

      {/* Kunde */}
      <Card className="p-4" data-testid="ctx-customer">
        <CtxHead icon={Building2}>{tr("Kunde")}</CtxHead>
        <div className="text-sm font-bold text-white">{c.company_name || t.company_name || "—"}</div>
        {c.domain && <a href={`https://${c.domain}`} target="_blank" rel="noreferrer" className="text-[12px] text-[#A3E635] inline-flex items-center gap-1 mt-0.5"><Globe className="w-3 h-3" /> {c.domain}</a>}
        <div className="mt-2 space-y-1">
          {c.contact_person && <IconRow icon={Users}>{c.contact_person}</IconRow>}
          {c.email && <IconRow icon={Mail}><a href={`mailto:${c.email}`} className="hover:text-white">{c.email}</a></IconRow>}
          {c.phone && <IconRow icon={Phone}><a href={`tel:${c.phone}`} className="hover:text-white">{c.phone}</a></IconRow>}
        </div>
        {t.client_id && (
          <button onClick={() => nav(`/admin/clients/${t.client_id}`)} data-testid="ctx-open-client"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-100">
            <ExternalLink className="w-3.5 h-3.5" /> {tr("Kunde öffnen")}
          </button>
        )}
      </Card>

      {/* Paket & Vertrag */}
      <Card className="p-4" data-testid="ctx-package">
        <CtxHead icon={PackageIcon}>{tr("Paket & Vertrag")}</CtxHead>
        <Row k={tr("Paket")} v={p.name || "—"} strong />
        {p.price_chf != null && <Row k={tr("Monatlich")} v={`CHF ${p.price_chf}.–`} />}
        <Row k={tr("Laufzeit")} v={p.contract_months ? `${p.contract_months} ${tr("Monate")}` : "—"} />
        {p.contract_until && <Row k={tr("Vertrag bis")} v={fmtDate(p.contract_until)} />}
        {(p.modules || []).length > 0 && (
          <div className="mt-2">
            <div className="text-[11px] text-gray-500 mb-1.5">{tr("Gebuchte Module")}</div>
            <div className="flex flex-wrap gap-1.5">
              {p.modules.map((m, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-gray-300">{m}</span>)}
            </div>
          </div>
        )}
        {t.client_id && (
          <button onClick={() => nav(`/admin/clients/${t.client_id}`)} data-testid="ctx-open-package"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-100">
            <ChevronRight className="w-3.5 h-3.5" /> {tr("Paketdetails öffnen")}
          </button>
        )}
      </Card>

      {/* Paketprüfung / Verrechnung */}
      {canWrite && <PaketPruefung entityType="support" entity={t} onChanged={onMutated} />}

      {/* Anhänge */}
      <Card className="p-4" data-testid="ctx-attachments">
        <CtxHead icon={Paperclip}>{tr("Anhänge")} {(t.attachments || []).length > 0 && <span className="text-gray-500">({t.attachments.length})</span>}</CtxHead>
        {(t.attachments || []).length === 0 ? <p className="text-xs text-gray-600">{tr("Keine Dateien.")}</p> : (
          <div className="space-y-1.5">
            {(t.attachments || []).slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs text-gray-300 min-w-0">
                <FileText className="w-3.5 h-3.5 text-[#A3E635] shrink-0" />
                <span className="truncate flex-1">{a.file_name}</span>
                <span className="text-[10px] text-gray-600 shrink-0">{fmtSize(a.file_size)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Verknüpfte Aufgaben */}
      <Card className="p-4" data-testid="ctx-tasks">
        <CtxHead icon={KanbanSquare}>{tr("Verknüpfte Aufgaben")}</CtxHead>
        {(t.tasks || []).length === 0 ? <p className="text-xs text-gray-600">{tr("Keine offenen Aufgaben.")}</p> : (
          <div className="space-y-1.5">
            {(t.tasks || []).map((tk) => (
              <div key={tk.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-gray-300">{tk.title}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 bg-black text-gray-400 shrink-0">{tr(tk.status)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Aktivität */}
      <Card className="p-4" data-testid="ctx-activity">
        <CtxHead icon={History}>{tr("Aktivität")}</CtxHead>
        {(t.activity || []).length === 0 ? <p className="text-xs text-gray-600">{tr("Keine Einträge.")}</p> : (
          <div className="space-y-2">
            {(t.activity || []).slice(0, 6).map((a) => (
              <div key={a.id} className="text-xs">
                <div className="text-gray-200">{a.action}</div>
                <div className="text-[10px] text-gray-600">{a.actor_name} · {fmtDateTime(a.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const CtxHead = ({ icon: Icon, children }) => (
  <h3 className="font-bold text-sm mb-2.5 flex items-center gap-2"><Icon className="w-4 h-4 text-[#A3E635]" /> {children}</h3>
);
const Row = ({ k, v, strong }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-white/[0.05] last:border-0">
    <span className="text-[12px] text-gray-500 shrink-0">{k}</span>
    <span className={`text-[12px] text-right truncate ${strong ? "font-bold text-white" : "text-gray-200"}`}>{v}</span>
  </div>
);
const IconRow = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-[12px] text-gray-300"><Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" /> <span className="truncate">{children}</span></div>
);

function renderChanges(c) {
  try {
    if (c.status) return `Status: ${c.status.von} → ${c.status.zu}`;
    if (c.prioritaet) return `Priorität: ${c.prioritaet.von} → ${c.prioritaet.zu}`;
    if (c.kategorie) return `Kategorie: ${c.kategorie.von} → ${c.kategorie.zu}`;
    if (c.zustaendig) return `Zuständig: ${c.zustaendig.von} → ${c.zustaendig.zu}`;
    if (c.aufgabe) return `Aufgabe: ${c.aufgabe}`;
    if (c.datei) return `Datei: ${c.datei}`;
    if (c.betreff) return c.betreff;
    return "";
  } catch { return ""; }
}

// ---------------- Neuer Supportfall ----------------
function NewTicketModal({ cfg, clients, onClose, onCreated, returnFocusRef }) {
  const { t: tr } = useI18n();
  const [form, setForm] = useState(EMPTY_NEW);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const clientInput = useRef(null);

  const errs = [];
  if (!form.client_id) errs.push(tr("Kunde / Projekt fehlt."));
  if (!form.subject.trim()) errs.push(tr("Betreff fehlt."));
  if (!form.message.trim()) errs.push(tr("Nachricht fehlt."));
  if (["hoch", "kritisch"].includes(form.priority) && (!form.assignee || form.assignee === "nicht zugewiesen")) errs.push(tr("Hohe oder kritische Priorität benötigt eine zuständige Person."));

  const save = async () => {
    if (errs.length) { toast.error(tr("Bitte Pflichtfelder prüfen.")); return; }
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const { data } = await api.post("/support", { ...form, due_date: form.due_date || null });
      toast.success(tr("Supportfall erstellt"));
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || tr("Konnte nicht speichern.")); }
    finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !savingRef.current) onClose(); }}>
      <DialogContent data-testid="new-ticket-modal" closeLabel={tr("Schliessen")} closeDisabled={saving}
        className="w-[calc(100vw_-_2rem)] max-w-2xl max-h-[90dvh] overflow-y-auto min-w-0 bg-[#121217] border-white/10 text-white rounded-2xl p-4 sm:p-6"
        onOpenAutoFocus={(e) => { e.preventDefault(); clientInput.current?.focus(); }}
        onCloseAutoFocus={(e) => { e.preventDefault(); returnFocusRef?.current?.focus(); }}
        onEscapeKeyDown={(e) => { if (savingRef.current) e.preventDefault(); }}
        onInteractOutside={(e) => { if (savingRef.current) e.preventDefault(); }}>
        <div className="pr-8">
          <DialogTitle className="text-lg font-bold flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-[#A3E635]" /> {tr("Neuer Supportfall")}</DialogTitle>
          <DialogDescription className="text-sm text-gray-400 mt-1">{tr("Erfassen Sie ein neues Anliegen für einen Kunden.")}</DialogDescription>
        </div>
        <fieldset disabled={saving} className="grid sm:grid-cols-2 gap-3 mt-4">
          <Lbl t={tr("Kunde / Projekt *")}>
            <select ref={clientInput} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="nt-client" className={inputCls}>
              <option value="">{tr("— wählen —")}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </Lbl>
          <Lbl t={tr("Kategorie")}>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="nt-category" className={inputCls}>
              {Object.entries(cfg.categories).map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}
            </select>
          </Lbl>
          <Lbl t={tr("Betreff *")} full><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="nt-subject" className={inputCls} placeholder={tr("Worum geht es?")} /></Lbl>
          <Lbl t={tr("Nachricht *")} full><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="nt-message" rows={3} className={inputCls} placeholder={tr("Details zum Supportfall…")} /></Lbl>
          <Lbl t={tr("Priorität")}>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="nt-priority" className={inputCls}>
              {PRIORITIES.map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}
            </select>
          </Lbl>
          <Lbl t={tr("Status")}>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="nt-status" className={inputCls}>
              {cfg.status_order.map((s) => <option key={s} value={s}>{tr(cfg.status_labels[s])}</option>)}
            </select>
          </Lbl>
          <Lbl t={tr("Zuständige Person")}>
            <select value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} data-testid="nt-assignee" className={inputCls}>
              {cfg.assignees.map((a) => <option key={a} value={a}>{tr(a)}</option>)}
            </select>
          </Lbl>
          <Lbl t={tr("Fälligkeit")}><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="nt-due" className={inputCls} /></Lbl>
          <Lbl t={tr("Interne Notiz (nur KEWAG)")} full><textarea value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })} data-testid="nt-note" rows={2} className={inputCls} placeholder={tr("Optional, nur intern sichtbar…")} /></Lbl>
        </fieldset>

        {errs.length > 0 && (
          <div className="mt-4 space-y-1.5" data-testid="nt-warnings">
            {errs.map((e, i) => <div key={i} className="text-sm text-red-300 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {e}</div>)}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} disabled={saving} data-testid="nt-cancel" className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-40">{tr("Abbrechen")}</button>
          <button onClick={save} disabled={saving || errs.length > 0} data-testid="nt-save"
            className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold py-2 px-5 rounded-lg transition-colors">
            {saving ? tr("Speichern…") : tr("Supportfall erstellen")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Aufgabe aus Supportfall ----------------
function TaskModal({ t, onClose, onDone }) {
  const { t: tr } = useI18n();
  const [title, setTitle] = useState(tr("Support: {x}").replace("{x}", t.subject));
  const [priority, setPriority] = useState(t.priority || "mittel");
  const [due, setDue] = useState("");
  const [next, setNext] = useState(tr("Supportfall lösen"));
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/support/${t.id}/convert-task`, { title, priority, due_date: due || null, next_action: next }); toast.success(tr("Aufgabe erstellt")); onDone(); }
    catch { toast.error(tr("Konnte Aufgabe nicht erstellen.")); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/70 backdrop-blur-sm" data-testid="ticket-task-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg bg-[#121217] border border-white/10 rounded-2xl p-6 my-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2"><KanbanSquare className="w-5 h-5 text-[#A3E635]" /><h3 className="text-lg font-bold tracking-tight">{tr("Aufgabe aus Supportfall")}</h3></div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <Lbl t={tr("Titel")}><input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ticket-task-title" className={inputCls} /></Lbl>
          <div className="grid grid-cols-2 gap-3">
            <Lbl t={tr("Priorität")}><select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls}>{PRIORITIES.map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}</select></Lbl>
            <Lbl t={tr("Fälligkeit")}><input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={inputCls} /></Lbl>
          </div>
          <Lbl t={tr("Nächste Aktion")}><input value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} /></Lbl>
          <p className="text-[11px] text-gray-500">{tr("Kunde, Betreff, Nachricht, Kategorie und Ticket-Verknüpfung werden automatisch übernommen. Die Aufgabe ist intern (KEWAG).")}</p>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{tr("Abbrechen")}</button>
          <button onClick={go} disabled={busy} data-testid="ticket-task-confirm" className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-5 rounded-lg disabled:opacity-50">{busy ? "…" : tr("Aufgabe erstellen")}</button>
        </div>
      </div>
    </div>
  );
}

const Lbl = ({ t, children, full }) => {
  const id = children.props["data-testid"] || children.props.id;
  const control = id ? React.cloneElement(children, { id }) : children;
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label htmlFor={id} className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</label>
      <div className="mt-1">{control}</div>
    </div>
  );
};
